import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Tabs,
  Tab,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Autocomplete,
  IconButton
} from '@mui/material';
import {
  Search,
  Add,
  Visibility,
  Person,
  History,
  Assessment,
  AccountBalance,
  AttachMoney,
  Upload,
  CreditCard,
  Edit
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';

import PersonalDetails from './PersonalDetails';
import FinanceDetails from './FinanceDetails';
import TransactionHistory from './TransactionHistory';
import PerformanceStats from './PerformanceStats';
import LoansInfo from './LoansInfo';
import LoanForm from './LoanForm';
import AdvanceForm from './AdvanceForm';
import ManualRecordDialog from './ManualRecordDialog'; // Import the new component
import { fetchMembers, fetchGroups, createMember, fetchMemberTransactions, fetchMemberAllocations, getMpesaTransactions, submitLoanApplication, allocateFunds } from './api';

const Users = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [newUserDialog, setNewUserDialog] = useState(false);
  const [loanTypeSelectionDialog, setLoanTypeSelectionDialog] = useState(false);
  const [loanApplicationDialog, setLoanApplicationDialog] = useState(false);
  const [manualRecordDialog, setManualRecordDialog] = useState(false); // New state for manual record dialog
  const [selectedLoanType, setSelectedLoanType] = useState('');
  const [showSensitiveInfo, setShowSensitiveInfo] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newMemberForm, setNewMemberForm] = useState({
    name: '',
    phone: '',
    id_number: '',
    email: '',
    location: '',
    group: '',
    initial_contribution: 0,
    registration_fee: 0,
    notes: ''
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const membersData = await fetchMembers(searchQuery, selectedGroup);
        setUsersData(membersData);
      } catch (error) {
        console.error("Error loading members:", error);
      } finally {
        setLoading(false);
      }
    };
  
    loadData();
  }, [searchQuery, selectedGroup]); 

  useEffect(() => {
    const loadGroups = async () => {
      try {
        const groupsData = await fetchGroups();
        setGroups(groupsData);
      } catch (error) {
        console.error("Error loading groups:", error);
      }
    };
  
    loadGroups();
  }, []); 

  // Update the search handler to call API
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // This will trigger the useEffect that loads data
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSubmit = async () => {
    console.log("Submitting data:", newMemberForm);
    try {
      await createMember({
        ...newMemberForm,
        group: parseInt(newMemberForm.group, 10),  // Ensure it's an integer
      });
      setNewUserDialog(false);
      const membersData = await fetchMembers(searchQuery, selectedGroup);
      setUsersData(membersData);
    } catch (error) {
      console.error("Error creating member:", error.response?.data || error);
      alert(JSON.stringify(error.response?.data)); // Show the error message
    }
  };

  const resetForm = () => {
    setNewMemberForm({
      name: '',
      phone: '',
      id_number: '',
      email: '',
      location: '',
      group: '',
      notes: ''
    });
  };

  const handleDialogClose = () => {
    setNewUserDialog(false);
    resetForm();
  };

  const handleLoanTypeSelection = (type) => {
    setSelectedLoanType(type);
    setLoanTypeSelectionDialog(false);
    setLoanApplicationDialog(true);
  };

  const handleLoanButtonClick = () => {
    setLoanTypeSelectionDialog(true);
  };

  const handleLoanTypeDialogClose = () => {
    setLoanTypeSelectionDialog(false);
  };

  const handleLoanDialogClose = () => {
    setLoanApplicationDialog(false);
  };

  // New handler for manual record dialog
  const handleManualRecordClick = () => {
    setManualRecordDialog(true);
  };

  const handleManualRecordClose = () => {
    setManualRecordDialog(false);
  };

  // Handler for allocating funds in manual record
  const handleManualAllocation = async (memberId, allocationData) => {
    try {
      const response = await allocateFunds(memberId, allocationData);
      console.log('Allocation successful:', response);
      
      // Refresh the members data to reflect changes
      const membersData = await fetchMembers(searchQuery, selectedGroup);
      setUsersData(membersData);
      
      return response;
    } catch (error) {
      console.error('Error allocating funds:', error);
      throw error;
    }
  };

  const UserDetails = ({ user }) => {
    const [userTab, setUserTab] = useState(0);
    const [transactions, setTransactions] = useState([]);
    const [allocations, setAllocations] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      const loadTransactions = async () => {
        if (userTab === 2) { // Only load when transaction tab is active
          setLoading(true);
          try {
            const data = await fetchMemberTransactions(user.id);
            setTransactions(data);
          } catch (error) {
            console.error('Error loading transactions:', error);
          } finally {
            setLoading(false);
          }
        }
      };

      loadTransactions();
    }, [userTab, user.id]);

    useEffect(() => {
      const loadAllocations = async () => {
        if (userTab === 2) { // Only load when transaction tab is active
          setLoading(true);
          try {
            const data = await fetchMemberAllocations(user.id);
            setAllocations(data);
          } catch (error) {
            console.error('Error loading transactions:', error);
          } finally {
            setLoading(false);
          }
        }
      };

      loadAllocations();
    }, [userTab, user.id]);

    // Define tab title text based on current tab
    const getTabTitle = () => {
      switch(userTab) {
        case 0: return "Personal Details";
        case 1: return "Financial Details";
        case 2: return "Transaction History";
        case 3: return "Performance Stats";
        case 4: return "Loans Info";
        default: return "Personal Details";
      }
    };

    return (
      <Card sx={{ mt: 3, mb: 3 }}>
        {/* Persistent Header - Always shows user name and ID regardless of tab */}
      <Box sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: user.is_active 
            ? "linear-gradient(90deg, rgba(25,118,210,1) 0%, rgba(236,26,35,1) 51%, rgba(167,206,53,1) 100%)"
            : "linear-gradient(90deg, rgba(107,114,128,1) 0%, rgba(75,85,99,1) 51%, rgba(55,65,81,1) 100%)",
          color: 'white',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          opacity: user.is_active ? 1 : 0.85
        }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Avatar sx={{ bgcolor: 'primary.dark', mr: 2 }}>{user.name[0]}</Avatar>
      <Box>
        <Typography variant="h5" sx={{ 
          fontSize: { xs: '1.2rem', sm: '1.5rem' }, // Responsive font size
          lineHeight: 1.2
        }}>
          {user.name}
        </Typography>
        <Typography variant="body2" sx={{ 
        opacity: 0.9,
        fontSize: { xs: '0.8rem', sm: '0.875rem' },
        textTransform: 'capitalize',
        fontStyle: user.is_active ? 'normal' : 'italic'
      }}>
        {user.is_active 
          ? (user.group_name || user.group || 'No Group')
          : `Was a member of ${user.group_name || user.group || 'No Group'}`
        }
      </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Chip
        label={user.member_id}
        color="default"
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          fontWeight: 'bold',
          '& .MuiChip-label': { color: 'primary.dark' }
        }}
      />
      {!user.is_active && (
        <Chip
          label="INACTIVE"
          size="small"
          sx={{
            backgroundColor: 'rgba(220, 38, 38, 0.9)',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.7rem'
          }}
        />
      )}
    </Box>
  </Box>

        {/* Tab Navigation */}
        <CardContent sx={{ pb: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
            <Tabs 
              value={userTab} 
              onChange={(e, val) => setUserTab(val)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  minWidth: { xs: 'auto', sm: 90 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '& .MuiSvgIcon-root': {
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }
                }
              }}
            >
              <Tab label="Personal" icon={<Person />} iconPosition="start" />
              <Tab label="Financial" icon={<AccountBalance />} iconPosition="start" />
              <Tab label="Transactions" icon={<History />} iconPosition="start" />
              <Tab label="Performance" icon={<Assessment />} iconPosition="start" />
              <Tab label="Loans" icon={<CreditCard />} iconPosition="start" />
            </Tabs>
          </Box>
        </CardContent>

        {/* Tab Content Container with Responsive Height */}
      <CardContent sx={{ 
        pt: 2,
        height: { xs: '350px', sm: '400px', md: '400px', lg: '410px' },
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ 
          flex: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: (theme) => theme.palette.mode === 'dark' ? '#2a2a2a' : '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: (theme) => theme.palette.mode === 'dark' ? '#555' : '#c1c1c1',
            borderRadius: '10px',
            '&:hover': {
              background: (theme) => theme.palette.mode === 'dark' ? '#777' : '#a8a8a8',
            },
          },
        }}>
            {userTab === 0 && (
              <PersonalDetails
                user={user}
                groups={groups}  // Pass groups prop
                onMigrationSuccess={async (response) => {
                  // Refresh members data after successful migration
                  console.log('Migration successful:', response);
                  const membersData = await fetchMembers(searchQuery, selectedGroup);
                  setUsersData(membersData);
                }}
                showSensitiveInfo={showSensitiveInfo}
                setShowSensitiveInfo={setShowSensitiveInfo}
              />
            )}

            {userTab === 1 && (
              <FinanceDetails user={user} />
            )}

            {userTab === 2 && (
              <Box sx={{ position: 'relative', minHeight: '100%' }}>
                {loading ? (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px'
                  }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TransactionHistory transactions={transactions} allocations={allocations} />
                )}
              </Box>
            )}

            {userTab === 3 && (
              <PerformanceStats
                metrics={{
                  attendance: user.attendance,
                  loanRepayment: user.loanRepayment,
                  riskScore: user.riskScore,
                  discipline: user.discipline
                }}
                performanceHistory={user.performanceHistory}
              />
            )}

            {userTab === 4 && (
              <LoansInfo userId={user.id} />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar sx={{ 
          gap: { xs: 1, sm: 2 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          py: { xs: 1, sm: 0 }
        }}>
          <TextField
            placeholder="Search users..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              flexGrow: 1, 
              maxWidth: { xs: '100%', sm: 400 },
              minWidth: { xs: '200px', sm: 'auto' }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewUserDialog(true)}
            size={window.innerWidth < 600 ? "small" : "medium"}
            sx={{ 
              minWidth: { xs: 'auto', sm: 'auto' },
              '& .MuiButton-startIcon': {
                mr: { xs: 0, sm: 1 }
              }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add New Member
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Add Member
            </Box>
          </Button>
          
          {/* New Manual Record Button */}
          <Button
            variant="contained"
            color="info"
            startIcon={<Edit />}
            onClick={handleManualRecordClick}
            size={window.innerWidth < 600 ? "small" : "medium"}
            sx={{ 
              minWidth: { xs: 'auto', sm: 'auto' },
              '& .MuiButton-startIcon': {
                mr: { xs: 0, sm: 1 }
              }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Manual Record
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Manual
            </Box>
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<AttachMoney />}
            onClick={handleLoanButtonClick}
            size={window.innerWidth < 600 ? "small" : "medium"}
            sx={{ 
              minWidth: { xs: 'auto', sm: 'auto' },
              '& .MuiButton-startIcon': {
                mr: { xs: 0, sm: 1 }
              }
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Loan Application
            </Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
              Loan
            </Box>
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: { xs: 1, sm: 2, md: 3 },
        pb: { xs: 12, sm: 10, md: 8 }
      }}>
        {loading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px'
          }}>
            <CircularProgress />
          </Box>
        ) : (
          usersData.map((user) => (
            <UserDetails key={user.id} user={user} />
          ))
        )}
      </Box>

      {/* Manual Record Dialog */}
      <ManualRecordDialog
        open={manualRecordDialog}
        onClose={handleManualRecordClose}
        groups={groups}
        usersData={usersData}
        onAllocate={handleManualAllocation}
      />

      {/* Add New Member Dialog */}
      <Dialog
        open={newUserDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            margin: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
          }
        }}
      >
        <DialogTitle>Add New Member</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={newMemberForm.name}
                onChange={(e) => setNewMemberForm({...newMemberForm, name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                required
                value={newMemberForm.phone}
                onChange={(e) => setNewMemberForm({...newMemberForm, phone: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="ID Number"
                required
                value={newMemberForm.id_number}
                onChange={(e) => setNewMemberForm({...newMemberForm, id_number: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                value={newMemberForm.email}
                onChange={(e) => setNewMemberForm({...newMemberForm, email: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Physical Address"
                value={newMemberForm.location}
                onChange={(e) => setNewMemberForm({...newMemberForm, location: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Select Group</InputLabel>
                <Select
                  label="Select Group"
                  value={newMemberForm.group}
                  onChange={(e) => setNewMemberForm({...newMemberForm, group: e.target.value})}
                >
                  {groups.map(group => (
                    <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={3}
                value={newMemberForm.notes}
                onChange={(e) => setNewMemberForm({...newMemberForm, notes: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleSubmit}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loan Application Dialog */}
        <Dialog
          open={loanApplicationDialog}
          onClose={handleLoanDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              margin: { xs: 1, sm: 2 },
              width: { xs: 'calc(100% - 16px)', sm: 'auto' }
            }
          }}
        >
          <DialogTitle>
            {selectedLoanType === 'advance' ? 'New Advance Loan Application' : 'New Long-term Loan Application'}
          </DialogTitle>
          <DialogContent>
            {selectedLoanType === 'advance' ? (
              <AdvanceForm
                usersData={usersData}
                onClose={(success) => {
                  handleLoanDialogClose();
                  if (success) {
                    // You might want to refresh data or show a success message
                  }
                }}
                selectedMemberId={null}
              />
            ) : (
              <LoanForm
                usersData={usersData}
                onClose={(success) => {
                  handleLoanDialogClose();
                  if (success) {
                    // You might want to refresh data or show a success message
                  }
                }}
                selectedMemberId={null}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleLoanDialogClose}>Cancel</Button>
          </DialogActions>
        </Dialog>
      <Dialog
        open={loanTypeSelectionDialog}
        onClose={handleLoanTypeDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            margin: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
          }
        }}
      >
        <DialogTitle>Select Loan Type</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
            <Button
              variant="contained"
              startIcon={<AttachMoney />}
              onClick={() => handleLoanTypeSelection('longterm')}
              fullWidth
              size="large"
            >
              Long Term Loan
            </Button>
            <Button
              variant="outlined"
              startIcon={<AttachMoney />}
              onClick={() => handleLoanTypeSelection('advance')}
              fullWidth
              size="large"
            >
              Advance Loan
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLoanTypeDialogClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;